import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import { find, includes, map, toString } from 'lodash';
import { format } from 'date-fns';
import { renderToString } from 'react-dom/server';
import { useTheme } from '@mui/material/styles';

import { Timescale } from 'src/types/models';
import { truncationValues } from 'src/components/Visualization/visualizationConstants';
import {
  formatParameters,
  IFormatOutputParameters,
} from 'src/components/Visualization/formatParameters';

import {
  IChartData,
  IChartDataSeries,
} from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel';

highchartsAccessibility(Highcharts);

window.Buffer = window.Buffer || require('buffer').Buffer;

Highcharts.AST.allowedTags.push('Box');
Highcharts.AST.allowedAttributes.push('data-html2canvas-ignore');

export const colors = [
  '#27aae1',
  '#9c27b0',
  '#e91e63',
  '#43a047',
  '#0d47a1',
  '#ba68c8',
  '#ff9800',
  '#00bfa5',
  '#304ffe',
  '#613aa5',
  '#ffcea3',
  '#cddc39',
  '#607d8b',
  '#cc79a7',
  '#795548',
];

interface ILineChartProps extends HighchartsReact.Props {
  chartTitle: string;
  yAxisTitle: string;
  prefix: string;
  suffix: string;
  formatKey: number;
  truncation: number;
  decimalPlaces: number;
  data: IChartData;
  timeScaleType: Timescale;
  dateFormat: string;
  isPanelCollapsed: boolean;
  showScenarios: boolean;
  selectedChartDataType: string;
  groupedChartData: (IChartDataSeries & { color: string; dataType: string })[];
}

const LineChart: FunctionComponent<ILineChartProps> = ({
  chartTitle,
  yAxisTitle,
  prefix,
  suffix,
  formatKey,
  truncation,
  decimalPlaces,
  data,
  timeScaleType,
  dateFormat,
  isPanelCollapsed,
  showScenarios,
  selectedChartDataType,
  groupedChartData,
}) => {
  const theme = useTheme();
  const [options, setOptions] = useState<Highcharts.Options>({
    title: {
      text: '',
    },
  });
  // const [selectAllLegends, setSelectAllLegends] = useState<boolean>(true);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const timeScaleFormat = useMemo(() => {
    switch (timeScaleType) {
      case Timescale.Weekly:
        return 'Week';
      case Timescale.Monthly:
        return 'Month';
      case Timescale.Quarterly:
        return 'Quarter';
      default:
        return 'Year';
    }
  }, [timeScaleType]);

  const formatType = useMemo(
    () =>
      (
        find(formatParameters, {
          formatKey,
        }) as IFormatOutputParameters
      ).numberType,
    [formatKey]
  );

  useEffect(() => {
    // Shared-tooltip labels (see tooltip below): a version suffix only when both published and preview series are
    // plotted, a scenario suffix only when the chart shows more than one scenario.
    const showVersionInTooltip =
      groupedChartData.some((item) => item.dataType === 'preview') &&
      groupedChartData.some((item) => item.dataType !== 'preview');
    const showScenarioInTooltip =
      showScenarios &&
      new Set(groupedChartData.map((item) => item.scenario)).size > 1;
    setOptions({
      chart: {
        zooming: {
          type: 'x',
          resetButton: {
            position: {
              align: 'left', // by default
              verticalAlign: 'top', // by default
              x: -10,
              y: 10,
            },
          },
        },
        type: 'line',
        // v2 compression — forked from v1's 500px. Adjust here only;
        // ScenarioManager's chart stays at the v1 default.
        height: '320px',
      },
      title: {
        text: '',
      },
      exporting: {
        enabled: false,
      },
      subtitle: {
        text: '',
      },
      yAxis: {
        title: {
          text: undefined,
        },
        labels: {
          formatter: function () {
            const self = this;
            return `${prefix}${Highcharts.numberFormat(
              (formatType === 'Percent'
                ? (self.value as number) * 100
                : (self.value as number)) /
                truncationValues[toString(truncation)],
              decimalPlaces,
              '.'
            )
              .toString()
              .replaceAll(' ', '')
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${suffix}`;
          },
          style: {
            fontSize: '13px',
          },
        },
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: map(data.Dates, (item) =>
          includes([Timescale.Weekly, Timescale.Monthly], timeScaleType)
            ? format(new Date(item.Date), dateFormat)
            : item.Date
        ),
        labels: {
          style: {
            fontSize: '13px',
          },
        },
      },
      plotOptions: {
        series: {
          boostThreshold: 2000,
          label: {
            enabled: false,
            connectorAllowed: false,
          },
          marker: {
            enabled: data.Dates.length === 1,
          },
        },
      },
      tooltip: {
        // Slim, non-sticky popover (B1, 2026-09-05) listing EVERY visible series at the hovered period (shared,
        // 2026-09-07: the single-series version made the executive hunt line by line; the InputDeltaChart tooltip
        // that shows all lines at once was the model). Same-named series are told apart by a version suffix when
        // the chart carries both published and preview data ("US · Orig." / "US · Recalc.", the table's Version
        // column) and by scenario when more than one scenario is plotted. hideDelay/stickOnContact stay at their
        // defaults so it can never wedge open again. Formatting reuses the chart's own FormatKey-derived
        // prefix/suffix/truncation/decimalPlaces so the hover matches the axis.
        useHTML: true,
        shared: true,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const formatValue = (raw: number) =>
            `${prefix}${Highcharts.numberFormat(
              (formatType === 'Percent' ? raw * 100 : raw) /
                truncationValues[toString(truncation)],
              decimalPlaces,
              '.'
            )
              .toString()
              .replaceAll(' ', '')
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${suffix}`;
          const points = this.points ?? [this];
          return renderToString(
            <div
              style={{
                padding: '6px 10px',
                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                fontSize: '12px',
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {timeScaleFormat} {this.x}
              </div>
              {points.map((point) => {
                const seriesOptions = point.series.userOptions as {
                  id?: string;
                  dataType?: string;
                  scenario?: string;
                };
                const label = [
                  point.series.name,
                  showScenarioInTooltip ? seriesOptions.scenario : undefined,
                  showVersionInTooltip
                    ? seriesOptions.dataType === 'preview'
                      ? 'Recalc.'
                      : 'Orig.'
                    : undefined,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <div key={seriesOptions.id ?? label}>
                    <span style={{ color: point.color as string }}>●</span>{' '}
                    {label}: <b>{formatValue(Number(point.y))}</b>
                  </div>
                );
              })}
            </div>
          );
        },
      },
      series: map(groupedChartData, (item) => {
        return {
          name: item.segment,
          data: structuredClone(item.values),
          type: 'line',
          id: `${item.dataType ?? 'pub'}_${item.id}`,
          lineWidth: 3,
          color: item.color,
          scenario: item.scenario,
          visible: item.visible,
          dashStyle: (item.dashStyle as Highcharts.DashStyleValue) || 'Solid',
          dataType: item.dataType,
        };
      }),
    });
  }, [
    chartTitle,
    yAxisTitle,
    prefix,
    suffix,
    formatKey,
    truncation,
    decimalPlaces,
    data,
    timeScaleType,
    dateFormat,
    selectedChartDataType,
    groupedChartData,
    showScenarios,
  ]);

  useEffect(() => {
    // Reflow after the panel-collapse transition completes so the chart
    // remeasures into its new container width. chartRef.current is a
    // ref — including it in deps doesn't add a useful trigger and
    // (under StrictMode) caused double-fires; dropped.
    const timer = setTimeout(() => {
      if (chartRef.current && chartRef.current?.chart) {
        (chartRef.current as HighchartsReact.RefObject).chart.reflow();
      }
    }, theme.transitions.duration.leavingScreen);
    return () => clearTimeout(timer);
  }, [isPanelCollapsed, theme.transitions.duration.leavingScreen]);

  return (
    // immutable={true} on HighchartsReact destroys and recreates the
    // chart on every options change, which dismissed open tooltips and
    // caused the visible "re-render on keystroke" flicker. With the
    // parent's memoized series + this component's React.memo wrap,
    // option changes are rare and intentional; let Highcharts patch in
    // place (default) instead of nuking the chart.
    <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
  );
};

// React.memo: shallow-compare props so parent re-renders don't churn
// the chart when the underlying data is unchanged. groupedChartData
// and data are memoized in GenerateChartPanel so reference equality
// holds across keystrokes-in-chat-input renders.
export default React.memo(LineChart);
